import getMinimumBoundingBox from '@state/layout/minimalBoundingBox'
import { useLayout } from '@state/layout/queries'
import {
  mirrorShelfOnXAxis, recalculateShelves, toTransformedShelf,
  unrotateShelfByOBB
} from '@state/shelf/transformations/geometric'

import type { TransformedLayoutResponse } from '@state/layout/types'

export function useRawLayout(): TransformedLayoutResponse | undefined {
  const { data: layout } = useLayout()

  if (!layout) return undefined

  const shelves = layout.floors[0]?.shelves ?? []
  const width = shelves.reduce((max, shelf) => Math.max(max, shelf.x + shelf.width), 0)
  const height = shelves.reduce((max, shelf) => Math.max(max, shelf.y + shelf.depth), 0)

  return {
    width,
    height,
    shelves: shelves.map(toTransformedShelf),
  }
}

export function useLayoutMirrored(): TransformedLayoutResponse | undefined {
  const { data: layout } = useLayout()

  if (!layout) return undefined

  const shelves = layout.floors[0]?.shelves ?? []
  const transformedShelves = shelves.map(toTransformedShelf)

  const { minY, maxY, minX, maxX } = transformedShelves.reduce((acc, shelf) => {
    return {
      minY: Math.min(acc.minY, shelf.y), maxY: Math.max(acc.maxY, shelf.y),
      minX: Math.min(acc.minX, shelf.x), maxX: Math.max(acc.maxX, shelf.x),
    }
  }, { minY: Infinity, maxY: -Infinity, minX: Infinity, maxX: -Infinity })

  return {
    width: maxX - minX,
    height: maxY - minY,
    shelves: transformedShelves.map(shelf => mirrorShelfOnXAxis(shelf, maxY - minY)),
  }
}

export function useLayoutUnrotated(): TransformedLayoutResponse | undefined {
  const { data: layout } = useLayout()

  if (!layout) return undefined

  const shelves = layout.floors[0]?.shelves ?? []
  const transformedShelves = shelves.map(toTransformedShelf)

  const { minY, maxY } = transformedShelves.reduce((acc, shelf) => {
    return {
      minY: Math.min(acc.minY, shelf.y), maxY: Math.max(acc.maxY, shelf.y),
      minX: Math.min(acc.minX, shelf.x), maxX: Math.max(acc.maxX, shelf.x),
    }
  }, { minY: Infinity, maxY: -Infinity, minX: Infinity, maxX: -Infinity })

  const mirroredShelves = transformedShelves.map(shelf => mirrorShelfOnXAxis(shelf, maxY - minY))
  const obb = getMinimumBoundingBox(mirroredShelves)

  return {
    width: obb?.width ?? 0,
    height: obb?.height ?? 0,
    shelves: mirroredShelves.map(shelf => unrotateShelfByOBB(shelf, obb)),
  }
}

export function useFinialVersion(): TransformedLayoutResponse | undefined {
  const { data: layout } = useLayout()

  if (!layout) return undefined

  const shelves = layout.floors[0]?.shelves ?? []
  return recalculateShelves(shelves)
}

